import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'primereact/dialog'
import Button from '../../../components/ui/Button'
import { RadioButton } from 'primereact/radiobutton'
import { InputTextarea } from 'primereact/inputtextarea'
import type { LookupItem } from '../types'

/** REF_TICKET_REJECT_REASON "Diğer" id'si. Seçilince not zorunlu/görünür. */
export const REJECT_REASON_OTHER_ID = -1

interface RejectReasonDialogProps {
  visible: boolean
  reasons: LookupItem[]
  submitting: boolean
  /** Figma: body üstünde mavi "PNR - Yolcu" satırı (ör. "RT4K9P - Ahmet Yılmaz"). */
  subtitle?: string
  onConfirm: (rejectReasonId: number, rejectNote?: string) => void
  onHide: () => void
}

/**
 * Ret Gerekçesi popup'ı (007 US2; 013 migrasyon: PrimeReact Dialog + RadioButton + InputTextarea,
 * bespoke `.pop-*` kaldırıldı). Tanımlı gerekçelerden biri (radyo) zorunludur; "Diğer" (id=-1)
 * seçilince red açıklaması alanı **görünür ve zorunlu** olur. Onayla ile onConfirm(reasonId, note?).
 */
export default function RejectReasonDialog({
  visible,
  reasons,
  submitting,
  subtitle,
  onConfirm,
  onHide,
}: Readonly<RejectReasonDialogProps>) {
  const { t } = useTranslation()
  const [reasonId, setReasonId] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<'reason' | 'note' | null>(null)

  // Popup her açılışta temiz başlar.
  useEffect(() => {
    if (visible) {
      setReasonId(null)
      setNote('')
      setError(null)
    }
  }, [visible])

  const isOther = reasonId === REJECT_REASON_OTHER_ID

  const submit = () => {
    if (reasonId == null) {
      setError('reason')
      return
    }
    if (isOther && !note.trim()) {
      setError('note')
      return
    }
    onConfirm(reasonId, note.trim() ? note.trim() : undefined)
  }

  const footer = (
    <div className="ui-dialog-footer">
      <Button
        label={t('tickets.reject.submit')}
        severity="danger"
        onClick={submit}
        loading={submitting}
      />
    </div>
  )

  return (
    <Dialog
      visible={visible}
      className="rej-dialog"
      header={t('tickets.reject.title')}
      footer={footer}
      onHide={onHide}
      modal
      dismissableMask={!submitting}
      closeOnEscape={!submitting}
      closable={!submitting}
      draggable={false}
      style={{ width: '26rem', maxWidth: '92vw' }}
    >
      {subtitle && <p className="pop-subtitle">{subtitle}</p>}
      <div className="rej-reasons" role="radiogroup" aria-label={t('tickets.reject.title')}>
        {reasons.map((r) => (
          <div key={r.id} className="rej-reason">
            <RadioButton
              inputId={`rr-${r.id}`}
              name="rejectReason"
              checked={reasonId === r.id}
              onChange={() => {
                setReasonId(r.id)
                setError(null)
              }}
            />
            <label htmlFor={`rr-${r.id}`}>{r.name}</label>
          </div>
        ))}
      </div>
      {error === 'reason' && <div className="rej-err">{t('tickets.reject.reasonRequired')}</div>}

      {isOther && (
        <>
          <InputTextarea
            className="rej-note"
            rows={3}
            value={note}
            aria-label={t('tickets.reject.noteLabel')}
            placeholder={t('tickets.reject.notePlaceholder')}
            onChange={(e) => {
              setNote(e.target.value)
              if (error === 'note') setError(null)
            }}
          />
          {error === 'note' && <div className="rej-err">{t('tickets.reject.noteRequired')}</div>}
        </>
      )}
    </Dialog>
  )
}
